import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useBranch } from '@/contexts/BranchContext';
import { FeatureGuard } from '@/components/subscription/FeatureGuard';
import { cn } from '@/lib/utils';
import {
  BarChart3,
  Package,
  ChefHat,
  AlertTriangle,
  MapPin,
  Settings,
  TrendingUp,
  RefreshCw,
  Scale,
  Lock,
  Crown
} from 'lucide-react';
import { MaterialsList } from './MaterialsList';
import { InventoryDashboard } from './InventoryDashboard';
import { RecipeManagementPage } from './RecipeManagementPage';
import { ExpensesManagement } from './ExpensesManagement';
import { PhysicalCountPage } from './PhysicalCountPage';
import InventoryAnalyticsPage from '@/features/inventory/pages/InventoryAnalyticsPage';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useLowStockMaterials, useInventoryMetrics } from '@/features/inventory/hooks/useInventory';
import { useInventoryRealtime } from '@/features/inventory/hooks/useInventoryRealtime';

/**
 * Refactored Inventory Management Page
 * 
 * Clean Structure - 5 Tabs:
 * 1. Overview - Dashboard with quick stats
 * 2. Materials - Manage raw materials
 * 3. Recipes - Create and manage recipes
 * 4. Analytics - Comprehensive analytics (NEW unified page)
 * 5. Expenses - Track operating expenses
 * 
 * Improvements:
 * - Uses new inventory services and hooks
 * - Stock alerts prominently displayed
 * - Clean, intuitive navigation
 * - Full Arabic translation support
 * - Responsive design
 */
export const InventoryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { branches } = useFilteredBranches();
  const [activeTab, setActiveTab] = useState('overview');
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const [refreshKey, setRefreshKey] = useState(0);

  // Get date range for metrics (last 30 days)
  const dateRange = React.useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }, []);

  // Get current branch
  const currentBranch = branches.find(b => b.id === selectedBranchId);

  // Fetch stock alerts
  const { data: lowStockMaterials } = useLowStockMaterials(selectedBranchId ?? '');
  const { data: inventoryMetrics } = useInventoryMetrics(
    selectedBranchId ?? '',
    dateRange
  );

  useInventoryRealtime(selectedBranchId ?? '');

  const criticalAlerts = inventoryMetrics?.stockAlerts?.filter(a => a.severity === 'critical') || [];
  const hasAlerts = ((inventoryMetrics?.stockAlerts?.length || 0) > 0);

  return (
    <div className="flex-1 space-y-3 md:space-y-4 lg:space-y-6 p-3 md:p-4 lg:p-6">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 -mx-3 md:-mx-4 lg:-mx-6 px-3 md:px-4 lg:px-6 py-3 md:py-4 border-b">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
                {t('dashboard.inventory.title')}
              </h1>
              <Badge variant="outline" className="flex items-center gap-1 flex-shrink-0 text-xs">
                <TrendingUp className="h-3 w-3" />
                <span className="hidden sm:inline">{t('dashboard.inventory.dashboard.beta_label')}</span>
              </Badge>
              <Button
                onClick={() => setRefreshKey(prev => prev + 1)}
                variant="ghost"
                size="icon"
                className="ml-auto md:ml-2 h-8 w-8"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground line-clamp-1 md:line-clamp-none">
              {t('dashboard.inventory.description')}
            </p>
            {currentBranch && (
              <p className="text-xs md:text-sm text-primary mt-1.5 flex items-center gap-1 flex-wrap">
                <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" />
                <span className="hidden sm:inline">{t('dashboard.inventory.dashboard.currently_viewing')}:</span>
                <strong>{currentBranch.name}</strong>
              </p>
            )}
          </div>

          {/* Branch Selector */}
          {branches.length > 1 && (
            <Select
              value={selectedBranchId || ''}
              onValueChange={setSelectedBranchId}
            >
              <SelectTrigger className="w-full md:w-56 lg:w-64 flex-shrink-0">
                <SelectValue placeholder={t('dashboard.inventory.dashboard.select_branch')} />
              </SelectTrigger>
              <SelectContent>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Stock Alerts Banner */}
      {hasAlerts && (
        <Alert variant={criticalAlerts.length > 0 ? 'destructive' : 'default'} className="mx-0">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <AlertDescription>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm block mb-1 sm:mb-0 sm:inline">
                  {t('dashboard.inventory.alerts.stock_alerts_message')}
                </span>
                <span className="text-xs sm:text-sm block sm:inline sm:ml-2">
                  {inventoryMetrics.outOfStockCount > 0 && (
                    <span className="font-semibold text-destructive">
                      {inventoryMetrics.outOfStockCount} {t('dashboard.inventory.alerts.out_of_stock')}
                    </span>
                  )}
                  {inventoryMetrics.outOfStockCount > 0 && inventoryMetrics.lowStockCount > 0 && <span className="mx-1">•</span>}
                  {inventoryMetrics.lowStockCount > 0 && (
                    <span className="text-orange-600">
                      {inventoryMetrics.lowStockCount} {t('dashboard.inventory.alerts.low_stock')}
                    </span>
                  )}
                </span>
              </div>
              <button
                onClick={() => setActiveTab('materials')}
                className="text-xs sm:text-sm font-medium underline hover:no-underline whitespace-nowrap flex-shrink-0 self-start sm:self-auto"
              >
                {t('dashboard.inventory.alerts.view_materials')}
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Branch Selection Warning */}
      {!selectedBranchId && branches.length > 0 && (
        <Alert className="mx-0">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {t('dashboard.inventory.dashboard.select_branch_prompt')}
          </AlertDescription>
        </Alert>
      )}

      {/* No Branches Warning */}
      {branches.length === 0 && (
        <Alert className="mx-0">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {t('dashboard.inventory.dashboard.no_branches')}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
        {/* Mobile Tab Bar - Segmented Control with More Menu */}
        <div className="md:hidden">
          <div className="flex gap-1 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('overview')}
              className={cn(
                "flex-1 py-2 px-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1",
                activeTab === 'overview'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground"
              )}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              {t('dashboard.inventory.tabs.overview')}
            </button>
            <button
              onClick={() => setActiveTab('materials')}
              className={cn(
                "flex-1 py-2 px-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1",
                activeTab === 'materials'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Package className="h-3.5 w-3.5" />
              {t('dashboard.inventory.tabs.materials')}
            </button>
            <Select
              value={['recipes', 'analytics', 'expenses', 'physical-count'].includes(activeTab) ? activeTab : ''}
              onValueChange={(value) => setActiveTab(value)}
            >
              <SelectTrigger
                className={cn(
                  "flex-1 h-auto py-2 px-2 border-0 text-xs font-medium rounded-md",
                  ['recipes', 'analytics', 'expenses', 'physical-count'].includes(activeTab)
                    ? "bg-background shadow-sm text-foreground"
                    : "bg-transparent text-muted-foreground"
                )}
              >
                <div className="flex items-center gap-1 w-full justify-center">
                  {activeTab === 'recipes' && <ChefHat className="h-3.5 w-3.5" />}
                  {activeTab === 'analytics' && <TrendingUp className="h-3.5 w-3.5" />}
                  {activeTab === 'expenses' && <Settings className="h-3.5 w-3.5" />}
                  {activeTab === 'physical-count' && <Scale className="h-3.5 w-3.5" />}
                  {!['recipes', 'analytics', 'expenses', 'physical-count'].includes(activeTab) && (
                    <Settings className="h-3.5 w-3.5" />
                  )}
                  <span className="truncate">
                    {activeTab === 'recipes' ? t('dashboard.inventory.tabs.recipes') :
                      activeTab === 'analytics' ? t('dashboard.inventory.tabs.analytics') :
                        activeTab === 'expenses' ? t('dashboard.inventory.tabs.expenses') :
                          activeTab === 'physical-count' ? 'Count' : 'More'}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recipes">
                  <div className="flex items-center gap-2">
                    <ChefHat className="h-4 w-4" />
                    {t('dashboard.inventory.tabs.recipes')}
                    <Crown className="h-3 w-3 text-amber-500" />
                  </div>
                </SelectItem>
                <SelectItem value="analytics">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    {t('dashboard.inventory.tabs.analytics')}
                    <Crown className="h-3 w-3 text-amber-500" />
                  </div>
                </SelectItem>
                <SelectItem value="expenses">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {t('dashboard.inventory.tabs.expenses')}
                    <Crown className="h-3 w-3 text-amber-500" />
                  </div>
                </SelectItem>
                <SelectItem value="physical-count">
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Physical Count
                    <Crown className="h-3 w-3 text-amber-500" />
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Desktop Tab Bar - Original horizontal tabs */}
        <div className="hidden md:block">
          <TabsList className="inline-flex w-auto h-auto p-1">
            <TabsTrigger value="overview" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <BarChart3 className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{t('dashboard.inventory.tabs.overview')}</span>
            </TabsTrigger>
            <TabsTrigger value="materials" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <Package className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{t('dashboard.inventory.tabs.materials')}</span>
            </TabsTrigger>
            <TabsTrigger value="recipes" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <ChefHat className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{t('dashboard.inventory.tabs.recipes')}</span>
              <Crown className="h-3 w-3 text-amber-500" />
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <TrendingUp className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{t('dashboard.inventory.tabs.analytics')}</span>
              <Crown className="h-3 w-3 text-amber-500" />
            </TabsTrigger>
            <TabsTrigger value="expenses" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{t('dashboard.inventory.tabs.expenses')}</span>
              <Crown className="h-3 w-3 text-amber-500" />
            </TabsTrigger>
            <TabsTrigger value="physical-count" className="gap-2 px-4 py-2 data-[state=active]:bg-background">
              <Scale className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">Physical Count</span>
              <Crown className="h-3 w-3 text-amber-500" />
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 md:space-y-6 mt-0">
          {selectedBranchId ? (
            <>
              <InventoryDashboard
                key={refreshKey}
                selectedBranchId={selectedBranchId}
              />

              {/* Feature Roadmap - Mobile Optimized */}
              <Card className="border-0 md:border shadow-sm">
                <CardHeader className="p-3 md:p-4 lg:p-6">
                  <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.dashboard.roadmap.title')}</CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    {t('dashboard.inventory.dashboard.roadmap.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                  {/* Mobile: Scrollable horizontal cards, Desktop: 3 column grid */}
                  <div className="flex md:grid md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 overflow-x-auto md:overflow-x-visible -mx-3 px-3 md:mx-0 md:px-0 hide-scrollbar snap-x snap-mandatory">
                    <div className="flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-auto space-y-2 md:space-y-3 p-3 md:p-0 bg-green-50/50 md:bg-transparent rounded-lg md:rounded-none snap-center">
                      <h4 className="font-semibold text-green-600 flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
                        <span className="text-base md:text-lg">✅</span>
                        {t('dashboard.inventory.dashboard.roadmap.available_now')}
                      </h4>
                      <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.materials')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.recipes')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.analytics')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.expenses')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.cost_tracking')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.waste_analysis')}</li>
                      </ul>
                    </div>

                    <div className="flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-auto space-y-2 md:space-y-3 p-3 md:p-0 bg-blue-50/50 md:bg-transparent rounded-lg md:rounded-none snap-center">
                      <h4 className="font-semibold text-blue-600 flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
                        <span className="text-base md:text-lg">🚀</span>
                        {t('dashboard.inventory.dashboard.roadmap.coming_soon')}
                      </h4>
                      <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.purchase_orders')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.suppliers')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.barcode')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.notifications')}</li>
                      </ul>
                    </div>

                    <div className="flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-auto space-y-2 md:space-y-3 p-3 md:p-0 bg-purple-50/50 md:bg-transparent rounded-lg md:rounded-none snap-center">
                      <h4 className="font-semibold text-purple-600 flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
                        <span className="text-base md:text-lg">🔮</span>
                        {t('dashboard.inventory.dashboard.roadmap.future')}
                      </h4>
                      <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.mobile_app')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.ai_forecasting')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.pos_integration')}</li>
                        <li>• {t('dashboard.inventory.dashboard.roadmap.features.realtime_tracking')}</li>
                      </ul>
                    </div>
                  </div>
                  {/* Mobile scroll indicator */}
                  <div className="flex md:hidden justify-center gap-1.5 mt-3">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-600"></div>
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-600"></div>
                    <div className="h-1.5 w-1.5 rounded-full bg-purple-600"></div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <EmptyState
              icon={BarChart3}
              title={t('dashboard.inventory.empty_states.select_branch')}
              description={t('dashboard.inventory.empty_states.select_branch_desc')}
            />
          )}
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials" className="space-y-4 md:space-y-6 mt-0">
          {selectedBranchId ? (
            <Card className="border-0 md:border shadow-none md:shadow-sm">
              <CardHeader className="px-3 md:px-6 py-3 md:py-4">
                <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
                  <Package className="h-4 w-4 md:h-5 md:w-5" />
                  {t('dashboard.inventory.materials.title')}
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t('dashboard.inventory.materials.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-3 md:px-6 pb-3 md:pb-6">
                <MaterialsList selectedBranchId={selectedBranchId} />
              </CardContent>
            </Card>
          ) : (
            <EmptyState
              icon={Package}
              title={t('dashboard.inventory.empty_states.select_branch')}
              description={t('dashboard.inventory.empty_states.materials_desc')}
            />
          )}
        </TabsContent>

        {/* Recipes Tab - Advanced Tier */}
        <TabsContent value="recipes" className="space-y-4 md:space-y-6 mt-0">
          <FeatureGuard feature="inventory_tier" requiredTier="advanced" showBanner={true}>
            {selectedBranchId ? (
              <RecipeManagementPage selectedBranchId={selectedBranchId} />
            ) : (
              <EmptyState
                icon={ChefHat}
                title={t('dashboard.inventory.empty_states.select_branch')}
                description={t('dashboard.inventory.empty_states.recipes_desc')}
              />
            )}
          </FeatureGuard>
        </TabsContent>

        {/* Analytics Tab - Advanced Tier */}
        <TabsContent value="analytics" className="space-y-4 md:space-y-6 mt-0">
          <FeatureGuard feature="inventory_tier" requiredTier="advanced" showBanner={true}>
            {selectedBranchId ? (
              <InventoryAnalyticsPage selectedBranchId={selectedBranchId} />
            ) : (
              <EmptyState
                icon={TrendingUp}
                title={t('dashboard.inventory.empty_states.select_branch')}
                description={t('dashboard.inventory.empty_states.analytics_desc')}
              />
            )}
          </FeatureGuard>
        </TabsContent>

        {/* Expenses Tab - Advanced Tier */}
        <TabsContent value="expenses" className="space-y-4 md:space-y-6 mt-0">
          <FeatureGuard feature="inventory_tier" requiredTier="advanced" showBanner={true}>
            {selectedBranchId ? (
              <ExpensesManagement selectedBranchId={selectedBranchId} />
            ) : (
              <EmptyState
                icon={Settings}
                title={t('dashboard.inventory.empty_states.select_branch')}
                description={t('dashboard.inventory.empty_states.expenses_desc')}
              />
            )}
          </FeatureGuard>
        </TabsContent>

        {/* Physical Count Tab - Advanced Tier */}
        <TabsContent value="physical-count" className="space-y-4 md:space-y-6 mt-0">
          <FeatureGuard feature="inventory_tier" requiredTier="advanced" showBanner={true}>
            {selectedBranchId ? (
              <PhysicalCountPage
                selectedBranchId={selectedBranchId}
                onCountCompleted={() => setRefreshKey(prev => prev + 1)}
              />
            ) : (
              <EmptyState
                icon={Scale}
                title={t('dashboard.inventory.empty_states.select_branch')}
                description="Select a branch to perform physical inventory count"
              />
            )}
          </FeatureGuard>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ===== HELPER COMPONENT =====

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <Card className="border-0 md:border shadow-none md:shadow-sm">
      <CardContent className="text-center py-8 md:py-12 px-4">
        <Icon className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground/50 mx-auto mb-3 md:mb-4" />
        <h3 className="text-base md:text-lg font-semibold mb-1.5 md:mb-2">{title}</h3>
        <p className="text-sm md:text-base text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
} 
